import { type IssueStatusChild, type IssueStatusType } from '@kcftech/prometheus-client';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { sdClient } from 'contexts/SDKClientContext';
import { queryClient } from 'utils/query';
import { type RecurseNonSdkClass } from 'utils/sdk_types';

const queryKey = 'issue-statuses';

const issueStatusesQueryOptions = queryOptions({
    queryKey: [queryKey],
    queryFn: () => sdClient.getIssueStatuses({}),
    staleTime: Infinity,
    gcTime: Infinity,
});

export function useIssueStatuses() {
    return useQuery(issueStatusesQueryOptions);
}

type PlainIssueStatus = RecurseNonSdkClass<IssueStatusChild>;

/** The cached statuses by type, per fetched list */
const statusesByType = new WeakMap<
    object,
    ReadonlyMap<IssueStatusType, PlainIssueStatus>
>();

/**
 * The issue status of type `statusType`, or `undefined` when the statuses
 * haven't been fetched (by `useIssueStatuses`). Each type has one status.
 */
export function getCachedIssueStatus(
    statusType: IssueStatusType,
): PlainIssueStatus | undefined {
    const data = queryClient.getQueryData(issueStatusesQueryOptions.queryKey);
    if (!data) {
        return undefined;
    }
    let byType = statusesByType.get(data);
    if (!byType) {
        byType = new Map(
            data.Result.map(({ Id, StatusName, StatusType }) => [
                StatusType,
                { Id, StatusName, StatusType },
            ]),
        );
        statusesByType.set(data, byType);
    }
    return byType.get(statusType);
}

/**
 * The issue status of type `statusType`: the cached one, or, when the
 * statuses haven't been fetched, one with a placeholder id. For rendering
 * changes ahead of a refetch, not for requests.
 */
export function getIssueStatusOrPlaceholder(
    statusType: IssueStatusType,
    statusName: string,
): PlainIssueStatus {
    return (
        getCachedIssueStatus(statusType) ?? {
            Id: `nil-${statusType}`,
            StatusName: statusName,
            StatusType: statusType,
        }
    );
}
